import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface OtherPaymentAccountSuggestion {
    id?: number | null;
    acc_code?: string | null;
    acc_name?: string | null;
}

export interface OtherPaymentSaveRow {
    account_name: string;
    account_no?: string;
    pay_to?: string;
    description?: string;
    amount: string;
}

export interface OtherPaymentsSaveResponse {
    doc_no: string;
    inserted_count: number;
    transaction_ids: number[];
    cash_paymode: string;
    cash_account: string;
    cash_transaction_type_id: number;
}

export interface OtherPaymentsHistoryItem {
    doc_no: string;
    created_at?: string | null;
    total_amount: number | string;
    line_count: number;
}

export interface OtherPaymentsDetailLine {
    id: number;
    doc_no: string;
    created_at?: string | null;
    account_no?: string | null;
    account_name?: string | null;
    pay_to?: string | null;
    description?: string | null;
    amount: number | string;
}

export const saveOtherPayments = async (rows: OtherPaymentSaveRow[]): Promise<OtherPaymentsSaveResponse> => {
    try {
        const response: ApiResponse<OtherPaymentsSaveResponse> = (
            await apiClient.post(
                "/other_payments/save",
                {
                    rows,
                },
                {
                    headers: {
                        "x-skip-auth-redirect": "true",
                    },
                },
            )
        ).data;

        if (!response.data) {
            throw new Error("Failed to save other payments - no data returned");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to save other payments");
    }
};

export const getRecentOtherPayments = async (limit = 10): Promise<OtherPaymentsHistoryItem[]> => {
    try {
        const response: ApiResponse<OtherPaymentsHistoryItem[] | null> = (
            await apiClient.get("/other_payments/recent", {
                params: {
                    limit,
                },
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to load recent payments");
    }
};

export const getOtherPaymentDocumentDetails = async (docNo: string): Promise<OtherPaymentsDetailLine[]> => {
    try {
        const response: ApiResponse<OtherPaymentsDetailLine[] | null> = (
            await apiClient.get(`/other_payments/${encodeURIComponent(docNo)}/details`, {
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to load payment details");
    }
};

export const searchOtherPaymentAccounts = async (query: string, limit = 10): Promise<OtherPaymentAccountSuggestion[]> => {
    try {
        const response: ApiResponse<OtherPaymentAccountSuggestion[] | null> = (
            await apiClient.get("/other_payments/account-suggestions", {
                params: {
                    q: query,
                    limit,
                },
                headers: {
                    "x-skip-auth-redirect": "true",
                },
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }

        throw new Error("Failed to search account suggestions");
    }
};